// Shared helpers for surfacing context-file usage stats. Used by both the
// interaction "context files read" panel and the context-hub management table.
import type { ContextEntityUsageFieldsFragment } from "src/graphql";
import { AgenticSettingsEntityType, ContextFileType } from "src/graphql/types";
import { COPY_REFERENCES_TAB_PATH } from "src/pages/context-hub/copy-references/detail/utils";

// Canonical display order for the coarse agent categories.
const CATEGORY_ORDER = ["ads", "data", "lcm", "other"];

// Orders the raw usage categories into the canonical display order.
export function sortUsageCategories(
  usage: ContextEntityUsageFieldsFragment | undefined,
): ContextEntityUsageFieldsFragment["categories"] {
  return [...(usage?.categories ?? [])].sort(
    (a, b) =>
      CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category),
  );
}

// Deep link to a context entity's detail page on Knowledge Files.
const CONTEXT_ENTITY_PATH = "/context-hub/knowledge/files";

export function contextEntityHref(entityId: string): string {
  return `${CONTEXT_ENTITY_PATH}/${encodeURIComponent(entityId)}`;
}

export const SKILLS_PATH = "/context-hub/knowledge/skills";

// A doc's or skill's Knowledge page. Skills, global ones included, live on the
// Skills tab; the Files tab's `files/<id>` link opens nothing for them.
export function knowledgeEntityHref(entity: {
  id: string;
  isManaged?: boolean | null;
  contextType?: string | null;
}): string {
  return entity.isManaged === true ||
    entity.contextType === ContextFileType.Skill
    ? `${SKILLS_PATH}/${encodeURIComponent(entity.id)}`
    : contextEntityHref(entity.id);
}

export const STYLES_PATH = "/context-hub/knowledge/styles";

// Deep link to a style's Context Hub detail page.
export function styleHref(styleId: string): string {
  return `${STYLES_PATH}/${encodeURIComponent(styleId)}`;
}

// Deep link to where a context file is actually managed. Each entity kind has
// its own Context Hub surface — the Files tab only lists plain
// context_markdown/context_file entities, so its `files/<id>` link opens
// nothing for the other kinds.
export function contextFileHref(file: {
  entityId: string;
  type: AgenticSettingsEntityType;
  channel?: string | null;
  contextType?: string | null;
}): string {
  switch (file.type) {
    case AgenticSettingsEntityType.CopyReferences:
      return file.channel
        ? `${COPY_REFERENCES_TAB_PATH}/${encodeURIComponent(file.channel)}`
        : COPY_REFERENCES_TAB_PATH;
    case AgenticSettingsEntityType.ContextMarkdown:
    case AgenticSettingsEntityType.ContextFile:
      return file.contextType === ContextFileType.Skill
        ? `${SKILLS_PATH}/${encodeURIComponent(file.entityId)}`
        : contextEntityHref(file.entityId);
    default:
      return contextEntityHref(file.entityId);
  }
}

// Templated builds read one ad template entity per frame size. Listing those
// alongside the guidance files buries what a "context read" surface is meant to
// show, so every such list drops them.
export function omitAdTemplateContextFiles<
  T extends { type: AgenticSettingsEntityType },
>(files: readonly T[]): T[] {
  return files.filter(
    (file) => file.type !== AgenticSettingsEntityType.AdTemplate,
  );
}

// Human labels for the coarse agent categories the backend groups usage by.
// Keyed by the raw enum values (`ads` | `lcm` | `data` | `other`).
const CATEGORY_LABELS: Record<string, string> = {
  ads: "Ads",
  lcm: "Lifecycle",
  data: "Data",
  other: "Other",
};

export function agentCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

// A read means the agent opened the file, not that it followed it. Kept
// neutral: a low read rate can be expected for narrowly-scoped files, so we
// don't editorialize about what drives it.
export const READ_RATE_INTERPRETATION =
  "A read means the agent opened the file, not that it followed it. " +
  "The agent decides to read the file based on its name and description.";

// Formats a 0–1 read rate as a whole-number percentage. Null (file never in a
// session) renders as an em dash.
export function formatReadRate(readRate: number | null | undefined): string {
  if (readRate == null) return "—";
  return `${Math.round(readRate * 100)}%`;
}

// Shown in place of a read rate when an agent always loads this context type
// in full: the file is always in context, so a read rate (and its 0% floor for
// files the agent never opens on demand) is meaningless.
export const ALWAYS_LOADED_LABEL = "Always loaded";
